import { createApi } from '@reduxjs/toolkit/query/react';
import type {
  IDashboard,
  WidgetCatalogItem,
  WidgetCompatibilityCheckResult,
  WidgetCompatibilityReason,
  WidgetCompatibilityReasonSeverity,
  WidgetCompatibilityWarning,
} from '@/lib/types';
import { deriveCompatibilitySeverity } from '@/lib/compatibilityConstants';
import { endpoints } from '@/services/api';
import { getAuthedBaseQuery } from '@/services/rtkAuthedBaseQuery';

const getApiBaseUrl = (): string => {
  // Priority 1: explicit env var (production builds, Cloud Run)
  if (import.meta.env.VITE_API_URL && import.meta.env.VITE_API_URL.trim() !== '') {
    return import.meta.env.VITE_API_URL;
  }
  // Priority 2: relative URL — routes through Vite dev proxy (/api → http://backend:3000)
  // The Vite proxy in vite.config.ts already handles /api → http://backend:3000.
  // This makes the request same-origin (no CORS) regardless of frontend hostname
  // (localhost, Tailscale, LAN — all work without backend CORS config changes).
  return '/api/v1';
};

interface WrappedResponse<T> {
  data?: T;
}

interface CompatibilityCheckArgs {
  dashboardId: string;
  kind: string;
}

interface CompatibilityPayload {
  compatible?: boolean;
  reasons?: unknown;
  severity?: unknown;
  warnings?: unknown;
  fixCta?: unknown;
  /** [T149-CONT-BE] Backend-canonical descriptor key after alias normalisation. */
  canonicalKind?: unknown;
  /** [T149-CONT-BE] Raw kind string sent by the client. */
  requestedKind?: unknown;
}

// Wave 6B: batch compatibility check result shape
interface BatchCompatibilityResult {
  compatible: boolean;
  reasons: WidgetCompatibilityReason[];
  severity: WidgetCompatibilityReasonSeverity;
  warnings?: WidgetCompatibilityWarning[];
  resolverName: string;
  isDispatchable: boolean;
  /** [T149-CONT-BE] Backend-canonical descriptor key after alias normalisation. */
  canonicalKind?: string;
  /** [T149-CONT-BE] Raw kind string sent by the client. */
  requestedKind?: string;
}

interface BatchCompatibilityCheckArgs {
  dashboardId: string;
  kinds: Array<{ kind: string; w?: number; h?: number }>;
}

interface BatchCompatibilityCheckResult {
  results: Record<string, BatchCompatibilityResult>;
  unknownKinds: string[];
}

interface ReplaceWidgetKindArgs {
  dashboardId: string;
  widgetId: string;
  kind: string;
}

interface DryRunWidgetPreviewArgs {
  dashboardId: string;
  kind: string;
  period?: string;
  itemLimit?: number;
  accountIds?: string[];
  categoryIds?: string[];
  series?: string[];
}

export interface DryRunWidgetPreviewResult {
  compatible: boolean;
  reasons: WidgetCompatibilityReason[];
  severity: WidgetCompatibilityReasonSeverity;
  warnings: WidgetCompatibilityWarning[];
  fixCta?: WidgetCompatibilityCheckResult['fixCta'];
  dryRun: true;
  evaluatedAt: string;
  /** [T149-CONT-BE] Backend-canonical descriptor key after alias normalisation. */
  canonicalKind?: string;
  /** [T149-CONT-BE] Raw kind string sent by the client. */
  requestedKind?: string;
}

function normaliseWarnings(warnings: unknown): WidgetCompatibilityWarning[] {
  if (!Array.isArray(warnings)) return [];

  return warnings.reduce<WidgetCompatibilityWarning[]>((acc, warning) => {
    if (typeof warning === 'string' && warning.trim().length > 0) {
      acc.push({ reason: warning, fixCta: null });
      return acc;
    }

    if (!warning || typeof warning !== 'object') {
      return acc;
    }

    const entry = warning as Record<string, unknown>;
    const reason = typeof entry.reason === 'string'
      ? entry.reason
      : (typeof entry.message === 'string' ? entry.message : null);
    if (!reason) return acc;

    const rawFixCta = entry.fixCta;
    const fixCta = rawFixCta && typeof rawFixCta === 'object'
      && typeof (rawFixCta as Record<string, unknown>).label === 'string'
      && typeof (rawFixCta as Record<string, unknown>).path === 'string'
      ? {
        label: (rawFixCta as Record<string, unknown>).label as string,
        path: (rawFixCta as Record<string, unknown>).path as string,
      }
      : null;

    acc.push({ reason, fixCta });
    return acc;
  }, []);
}

function normaliseReasons(reasons: unknown): WidgetCompatibilityReason[] {
  if (!Array.isArray(reasons)) return [];

  return reasons.reduce<WidgetCompatibilityReason[]>((acc, reason) => {
    if (!reason || typeof reason !== 'object') return acc;

    const entry = reason as Record<string, unknown>;
    if (typeof entry.code !== 'string' || typeof entry.message !== 'string') return acc;

    const severity = typeof entry.severity === 'string'
      && ['error', 'warn', 'info'].includes(entry.severity)
      ? entry.severity as WidgetCompatibilityReasonSeverity
      : 'warn';

    acc.push({
      code: entry.code,
      message: entry.message,
      severity,
    });
    return acc;
  }, []);
}

function normaliseFixCta(value: unknown): WidgetCompatibilityCheckResult['fixCta'] {
  if (!value || typeof value !== 'object') return null;
  const entry = value as Record<string, unknown>;
  if (typeof entry.label === 'string' && typeof entry.path === 'string') {
    return { label: entry.label, path: entry.path };
  }
  return null;
}

function normaliseCompatibilityResult(payload: CompatibilityPayload): WidgetCompatibilityCheckResult {
  const warnings = normaliseWarnings(payload.warnings);
  const reasons = normaliseReasons(payload.reasons);
  const severity = deriveCompatibilitySeverity(reasons, payload.severity, warnings) ?? 'info';
  const compatible = payload.compatible ?? true;

  return {
    compatible,
    reasons,
    severity,
    warnings,
    fixCta: normaliseFixCta(payload.fixCta),
    canonicalKind: typeof payload.canonicalKind === 'string' ? payload.canonicalKind : undefined,
    requestedKind: typeof payload.requestedKind === 'string' ? payload.requestedKind : undefined,
  };
}

export const dashboardWidgetApi = createApi({
  reducerPath: 'dashboardWidgetApi',
  baseQuery: getAuthedBaseQuery(getApiBaseUrl()),
  tagTypes: ['Dashboard', 'WidgetCatalog'],  // [T149-OBJ3] Added WidgetCatalog tag for cache freshness
  endpoints: (builder) => ({
    getWidgetCatalog: builder.query<WidgetCatalogItem[], void>({
      query: () => endpoints.dashboards.widgetCatalog,
      transformResponse: (response: WrappedResponse<WidgetCatalogItem[]> | WidgetCatalogItem[]) => (
        Array.isArray(response) ? response : (response.data ?? [])
      ),
      providesTags: ['WidgetCatalog'],  // [T149-OBJ3] Tag catalog queries for cache invalidation on widget updates
    }),
    checkWidgetCompatibility: builder.mutation<WidgetCompatibilityCheckResult, CompatibilityCheckArgs>({
      query: ({ dashboardId, kind }) => ({
        url: endpoints.dashboards.widgetCompatibilityCheck(dashboardId),
        method: 'POST',
        body: { kind },
      }),
      transformResponse: (
        response: WrappedResponse<WidgetCompatibilityCheckResult> | WidgetCompatibilityCheckResult,
      ) => {
        const resolved = 'data' in (response as WrappedResponse<WidgetCompatibilityCheckResult>)
          ? (response as WrappedResponse<WidgetCompatibilityCheckResult>).data as WidgetCompatibilityCheckResult
          : (response as WidgetCompatibilityCheckResult);
        return normaliseCompatibilityResult((resolved ?? {}) as CompatibilityPayload);
      },
    }),
    batchCheckWidgetCompatibility: builder.mutation<BatchCompatibilityCheckResult, BatchCompatibilityCheckArgs>({
      query: ({ dashboardId, kinds }) => ({
        url: endpoints.dashboards.batchCompatibilityCheck(dashboardId),
        method: 'POST',
        body: { kinds },
      }),
      transformResponse: (response: WrappedResponse<BatchCompatibilityCheckResult> | BatchCompatibilityCheckResult) => {
        const wrapped = response as WrappedResponse<BatchCompatibilityCheckResult>;
        const payload: BatchCompatibilityCheckResult = wrapped.data ?? (response as BatchCompatibilityCheckResult);

        const rawResults = payload?.results ?? {};
        const results: Record<string, BatchCompatibilityResult> = {};

        for (const [kind, entry] of Object.entries(rawResults)) {
          const normalized = normaliseCompatibilityResult((entry ?? {}) as CompatibilityPayload);
          const rawEntry = (entry ?? {}) as unknown as Record<string, unknown>;
          const resolverName = typeof rawEntry.resolverName === 'string'
            ? rawEntry.resolverName as string
            : 'unknown';
          const isDispatchable = Boolean(rawEntry.isDispatchable);
          results[kind] = {
            compatible: normalized.compatible,
            reasons: normalized.reasons ?? [],
            severity: normalized.severity ?? 'info',
            warnings: normalized.warnings,
            resolverName,
            isDispatchable,
            canonicalKind: normalized.canonicalKind,
            requestedKind: normalized.requestedKind,
          };
        }

        return {
          results,
          unknownKinds: payload?.unknownKinds ?? [],
        };
      },
    }),
    replaceWidgetKind: builder.mutation<IDashboard, ReplaceWidgetKindArgs>({
      query: ({ dashboardId, widgetId, kind }) => ({
        url: endpoints.dashboards.replaceWidgetKind(dashboardId, widgetId),
        method: 'PUT',
        body: { kind },
      }),
      transformResponse: (response: WrappedResponse<IDashboard> | IDashboard) => (
        'data' in (response as WrappedResponse<IDashboard>)
          ? (response as WrappedResponse<IDashboard>).data as IDashboard
          : (response as IDashboard)
      ),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'Dashboard', id: arg.dashboardId },
        'WidgetCatalog',  // [T149-OBJ3] Invalidate all cached catalog queries on widget kind replace
      ],
    }),
    dryRunWidgetPreview: builder.mutation<DryRunWidgetPreviewResult, DryRunWidgetPreviewArgs>({
      query: ({ dashboardId, kind, period, itemLimit, accountIds, categoryIds, series }) => ({
        url: endpoints.dashboards.widgetCompatibilityCheck(dashboardId),
        method: 'POST',
        body: {
          kind,
          dryRun: true,
          ...(period !== undefined ? { period } : {}),
          ...(itemLimit !== undefined ? { itemLimit } : {}),
          ...(accountIds !== undefined ? { accountIds } : {}),
          ...(categoryIds !== undefined ? { categoryIds } : {}),
          ...(series !== undefined ? { series } : {}),
        },
      }),
      transformResponse: (
        response: WrappedResponse<WidgetCompatibilityCheckResult> | WidgetCompatibilityCheckResult,
      ) => {
        const resolved = 'data' in (response as WrappedResponse<WidgetCompatibilityCheckResult>)
          ? (response as WrappedResponse<WidgetCompatibilityCheckResult>).data as WidgetCompatibilityCheckResult
          : (response as WidgetCompatibilityCheckResult);
        const normalised = normaliseCompatibilityResult((resolved ?? {}) as CompatibilityPayload);
        return {
          compatible: normalised.compatible,
          reasons: normalised.reasons ?? [],
          severity: normalised.severity ?? 'info',
          warnings: normalised.warnings ?? [],
          fixCta: normalised.fixCta ?? undefined,
          dryRun: true,
          evaluatedAt: new Date().toISOString(),
          canonicalKind: normalised.canonicalKind,
          requestedKind: normalised.requestedKind,
        };
      },
    }),
  }),
});

export const {
  useGetWidgetCatalogQuery,
  useCheckWidgetCompatibilityMutation,
  useBatchCheckWidgetCompatibilityMutation,
  useReplaceWidgetKindMutation,
  useDryRunWidgetPreviewMutation,
} = dashboardWidgetApi;